// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';

// constants
import { CDL_USER_INTENT } from '../../constants';

/**
 * The user intents the payment grid's own inputs and cells carry.
 *
 * The Residual row belongs here for the same reason the rate row does (DE762937): its edits are grid edits, so
 * they must not move the deal's engine. Left out, `UPDATED_PROGRAM_RESIDUAL_*` fell through to
 * `UPQ_TRIGGER_ACTIONS`, the lease flipped off program-quotes on every grid residual edit, Auto Select
 * unticked itself, and anything keyed on the deal endpoint — the grid's own residual fallback included — read
 * the flip as the user turning Auto Select off.
 */
const GRID_USER_INTENTS: ReadonlySet<string> = new Set([
    CDL_USER_INTENT.PAYMENT_GRID_SELL_RATE,
    CDL_USER_INTENT.PAYMENT_GRID_RESIDUAL,
    CDL_USER_INTENT.PAYMENT_GRID_TEXT_ENTRY,
    CDL_USER_INTENT.PAYMENT_GRID_CELL_SELECTED
]);

/**
 * Whether an action, or the payment a response answers, came from the payment grid rather than the deal screen.
 *
 * Shared because both endpoint middlewares need the same answer, and they are mutually exclusive by feature toggle - so
 * a guard written in only one stops applying the moment that toggle flips.
 *
 * Why either has to skip the grid: the grid carries each column's engine on the request itself, so it never needs the
 * deal's engine moved, and moving it is destructive - a rate edit unpins every other column, and a cell click reaches
 * `invalidateProgramAndRefetch`, whose `partialResetOverrides()` discards the per-column rates the user typed.
 *
 * **`gridAction` alone is not the test**: every payment action is fanned out into row calls stamped with it while the
 * grid is open, so it means "the grid produced this call". `dealScreenOriginated` separates the two.
 */
const isGridStamped = (gridMeta: AnyFSA['meta'] | undefined): boolean =>
    Boolean(gridMeta?.gridAction) && !gridMeta?.dealScreenOriginated;

export const isPaymentGridOriginated = (action: AnyFSA): boolean => {
    // Grid row payments carry the grid meta; the grid's inputs and its cell selection carry a grid intent.
    const isGridEdit = isGridStamped(action.meta?.paymentGrid) || GRID_USER_INTENTS.has(action.meta?.userIntent as string);
    const isGridResponse = isGridStamped((action.meta?.originalAction as AnyFSA | undefined)?.meta?.paymentGrid);

    return isGridEdit || isGridResponse;
};
